import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, HelpCircle, Search } from 'lucide-react'
import { faqApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { LoadingSpinner } from '../../components/ui/EmptyState'
import { cn } from '../../lib/utils'
import type { FAQ } from '../../types'

export function FAQPage() {
  const [search, setSearch] = useState('')
  const [modellFilter, setModellFilter] = useState('alle')
  const [openId, setOpenId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['faq', modellFilter],
    queryFn: () => faqApi.list(modellFilter !== 'alle' ? modellFilter : undefined),
  })

  const filtered = (data ?? []).filter((f) =>
    [f.frage, f.antwort, f.kategorie].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  const grouped = filtered.reduce<Record<string, FAQ[]>>((acc, f) => {
    if (!acc[f.kategorie]) acc[f.kategorie] = []
    acc[f.kategorie].push(f)
    return acc
  }, {})

  return (
    <>
      <TopBar title="FAQ & Selbsthilfe" />
      <div className="px-4 py-3 space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <HelpCircle size={18} className="text-blue-600" />
            <p className="font-semibold text-blue-900">Schnelle Hilfe</p>
          </div>
          <p className="text-sm text-blue-700">Häufige Probleme und Lösungen für alle Vaporex-Geräte.</p>
        </div>

        <Input placeholder="Suche in FAQ..." value={search} onChange={(e) => setSearch(e.target.value)} icon={<Search size={16} />} />

        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
          {['alle', 'DS2', 'DS4'].map((m) => (
            <button key={m} onClick={() => setModellFilter(m)}
              className={cn('shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors',
                modellFilter === m ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
              )}>
              {m === 'alle' ? 'Alle Geräte' : m}
            </button>
          ))}
        </div>

        {isLoading ? <LoadingSpinner /> : Object.entries(grouped).map(([kategorie, items]) => (
          <div key={kategorie}>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">{kategorie}</h3>
            <div className="space-y-2">
              {items.map((faq) => (
                <Card key={faq.id} padding={false} className="overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-4 text-left"
                    onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                  >
                    <div className="flex-1 mr-4">
                      <p className="font-medium text-sm text-gray-900">{faq.frage}</p>
                      {faq.fuer_modell !== 'alle' && (
                        <span className="text-xs text-blue-600 mt-0.5 block">{faq.fuer_modell}</span>
                      )}
                    </div>
                    {openId === faq.id
                      ? <ChevronUp size={18} className="text-gray-400 shrink-0" />
                      : <ChevronDown size={18} className="text-gray-400 shrink-0" />
                    }
                  </button>
                  {openId === faq.id && (
                    <div className="px-4 pb-4">
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{faq.antwort}</p>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        ))}

        {!isLoading && filtered.length === 0 && (
          <Card>
            <div className="text-center py-8">
              <HelpCircle size={32} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Keine FAQ-Einträge gefunden</p>
            </div>
          </Card>
        )}
      </div>
    </>
  )
}
